import React, { useState, useEffect } from 'react';
import { Search, Globe, Users, Star, TrendingUp, ArrowUpDown } from 'lucide-react';
import companyData from '../data/AI_compaines.json';
import PageLayout from '../components/PageLayout';

const Directory = () => {
  const [companies, setCompanies] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [sortBy, setSortBy] = useState('market_cap'); // Default sort

  useEffect(() => {
    setCompanies(companyData.companies);
  }, []);

  // 1. Filter & Sort Logic
  const processedCompanies = companies
    .filter(company => {
      const matchesSearch = company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            company.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === 'All' || company.category === selectedCategory;
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      if (sortBy === 'rating') return b.rating - a.rating; // Highest first
      if (sortBy === 'employees') {
        // Clean string "30,000" to number 30000 for accurate sorting
        const numA = parseInt(a.employees.replace(/,/g, '')) || 0;
        const numB = parseInt(b.employees.replace(/,/g, '')) || 0;
        return numB - numA;
      }
      if (sortBy === 'market_cap') {
        // Simple heuristic: assuming 'T' for Trillion and 'B' for Billion
        const val = (str) => {
          if (!str) return 0;
          const num = parseFloat(str);
          if (str.includes('T')) return num * 1000;
          return num;
        };
        return val(b.market_cap) - val(a.market_cap);
      }
      return 0;
    });

  const categories = ['All', ...new Set(companies.map(c => c.category))];

  return (
    <PageLayout>
      <div className="min-h-screen bg-gray-50 p-6 font-sans">
        {/* Header Section */}
        <div className="max-w-7xl mx-auto mb-10">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Global AI Directory</h1>
          <p className="text-gray-600">Discover industry leaders shaping the future of Intelligence.</p>
          
          {/* Controls Bar */}
          <div className="mt-8 flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by company or technology..."
                className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-500 outline-none transition-all shadow-sm bg-white"
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="flex flex-col md:flex-row gap-4">
              {/* Category Filter */}
              <select 
                className="bg-white border border-gray-200 px-4 py-3 rounded-xl shadow-sm outline-none focus:ring-2 focus:ring-blue-500 font-medium text-gray-700 min-w-[160px]"
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
              </select>

              {/* Sort Dropdown */}
              <div className="relative min-w-[200px]">
                <ArrowUpDown className="absolute left-3 top-1/2 -transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                <select 
                  className="w-full bg-white border border-gray-200 pl-10 pr-4 py-3 rounded-xl shadow-sm outline-none focus:ring-2 focus:ring-blue-500 font-medium text-gray-700 appearance-none"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="name">Sort by: Name (A-Z)</option>
                  <option value="rating">Sort by: Highest Rating</option>
                  <option value="employees">Sort by: Team Size</option>
                  <option value="market_cap">Sort by: Valuation/Funding</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Grid Section */}
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {processedCompanies.map((company) => (
            <div key={company.id} className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow border border-gray-100 flex flex-col justify-between">
              <div>
                <div className="flex justify-between items-start mb-4">
                  <img 
                    src={`https://logo.clearbit.com/${company.domain}`} 
                    alt={company.name}
                    className="w-14 h-14 rounded-lg object-contain bg-gray-50 p-1 border"
                    onError={(e) => { e.target.src = `https://ui-avatars.com/api/?name=${company.name}&background=random`; }}
                  />
                  <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                    company.status === 'Public' ? 'bg-green-100 text-green-700' : 'bg-purple-100 text-purple-700'
                  }`}>
                    {company.status} {company.ticker && `(${company.ticker})`}
                  </span>
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-2">{company.name}</h3>
                <p className="text-gray-600 text-sm line-clamp-2 mb-4 leading-relaxed">
                  {company.description}
                </p>

                {/* Stats Row */}
                <div className="grid grid-cols-2 gap-3 mb-6 text-xs text-gray-500 font-bold border-t pt-4">
                  <div className="flex items-center gap-2"><Users size={14} /> {company.employees}</div>
                  <div className="flex items-center gap-2"><Star size={14} className="text-yellow-500 fill-yellow-500" /> {company.rating}/5.0</div>
                  <div className="flex items-center gap-2"><Globe size={14} /> {company.headquarters}</div>
                  <div className="flex items-center gap-2"><TrendingUp size={14} /> {company.market_cap || company.valuation}</div>
                </div>

                {/* Tech Stack Chips */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {company.tech_stack.map(tech => (
                    <span key={tech} className="bg-blue-50 text-blue-600 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>

              <a 
                href={`https://${company.domain}`} 
                target="_blank" 
                rel="noreferrer"
                className="mt-auto w-full text-center bg-gray-900 text-white py-3 rounded-xl font-bold hover:bg-blue-600 transition-colors flex items-center justify-center gap-2 shadow-sm"
              >
                Visit Website <Globe size={16} />
              </a>
            </div>
          ))}
        </div>
        
        {processedCompanies.length === 0 && (
          <div className="text-center py-20 text-gray-400 font-medium bg-white rounded-3xl border border-dashed border-gray-200">
            No companies found matching your search.
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default Directory;